import React, { useEffect, useRef, useState } from 'react';
import './SplineBackground.css';

const RUNTIME_MODULE_URL = 'https://esm.sh/@splinetool/runtime@1.9.75';

const getViewportSize = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

const requestIdle = (callback, timeout = 1500) => {
  if (typeof window.requestIdleCallback === 'function') {
    return window.requestIdleCallback(callback, { timeout });
  }
  return window.setTimeout(callback, 200);
};

const cancelIdle = (handle) => {
  if (typeof window.cancelIdleCallback === 'function') {
    window.cancelIdleCallback(handle);
  } else {
    window.clearTimeout(handle);
  }
};

const lockSplineSize = (canvas, app, width, height) => {
  if (!canvas || !width || !height) return;

  canvas.width = width;
  canvas.height = height;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  canvas.style.marginTop = '0';

  app?.setSize?.(width, height);
};

// react-snap prerenders with userAgent "ReactSnap". The Spline WebGL runtime
// must never load there: it renders nothing useful into static HTML and its
// module/scene downloads keep the network busy past react-snap's 30s
// navigation timeout on small build machines.
const isPrerender =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

const SplineBackground = ({ sceneUrl, embedded = false, className = '' }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const appRef = useRef(null);
  const [dimensions, setDimensions] = useState(() => (embedded ? null : getViewportSize()));

  useEffect(() => {
    if (!embedded) {
      const onResize = () => setDimensions(getViewportSize());
      onResize();
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }

    const container = containerRef.current;
    if (!container) return undefined;

    const measure = () => {
      const rect = container.getBoundingClientRect();
      setDimensions({
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      });
    };

    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(container);
    window.addEventListener('resize', measure);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [embedded, sceneUrl]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (isPrerender) return undefined;
    if (!canvas || !sceneUrl || !dimensions?.width || !dimensions?.height) return undefined;

    let disposed = false;
    const { width, height } = dimensions;

    lockSplineSize(canvas, null, width, height);

    const loadScene = async () => {
      try {
        const { Application } = await import(
          /* webpackIgnore: true */
          RUNTIME_MODULE_URL
        );
        if (disposed || !canvasRef.current) return;

        const app = new Application(canvasRef.current);
        appRef.current = app;
        app.setSize(width, height);

        await app.load(sceneUrl);
        if (disposed) return;

        app.setSize(width, height);
        // Our canvases are rendered with pointer-events: none to avoid blocking UI,
        // so we must listen for mouse/touch events globally (window/document).
        app.setGlobalEvents(true);
      } catch (error) {
        console.error('Failed to load Spline background', error);
      }
    };

    // Defer the runtime import + scene load until the browser is idle, so this
    // heavy WebGL work never competes with initial paint / hydration on load.
    const idleHandle = requestIdle(loadScene);

    return () => {
      disposed = true;
      cancelIdle(idleHandle);
      appRef.current?.dispose?.();
      appRef.current = null;
    };
  }, [sceneUrl, dimensions?.width, dimensions?.height, embedded]);

  const containerClassName = [
    embedded ? 'spline-embedded' : 'spline-overlay',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (!dimensions?.width || !dimensions?.height) {
    return embedded ? (
      <div ref={containerRef} className={containerClassName} aria-hidden="true" />
    ) : null;
  }

  const { width, height } = dimensions;

  // In embedded mode the wrapper is measured by the ResizeObserver above, so
  // it must stay sized by CSS (`.spline-embedded { inset: 0 }`) rather than
  // an inline pixel size here — pinning it to whatever the first measurement
  // happened to be (which can be wrong if it races a lazy-loaded route's CSS)
  // would make the wrapper stop tracking its parent, freezing it at that
  // size forever. Only the non-embedded, viewport-sized overlay is safe to
  // size inline, since that size comes from window dimensions, not from
  // measuring this same element.
  const wrapperStyle = embedded
    ? { pointerEvents: 'none' }
    : { width: `${width}px`, height: `${height}px`, pointerEvents: 'none' };

  return (
    <div
      ref={embedded ? containerRef : undefined}
      className={containerClassName}
      style={wrapperStyle}
      aria-hidden="true"
    >
      <canvas
        ref={canvasRef}
        className="spline-overlay-canvas"
        style={{ pointerEvents: 'none' }}
      />
    </div>
  );
};

export default SplineBackground;
